import React from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { colors, margins } from "style";
import Markdown from "markdown-to-jsx";
import { AiRating, RegenerateAiSummaryButton } from "components/Profile";
import { RATING_CONTENT_TYPES, RATING_RESOURCE_TYPES } from "constants/index";
import { TextLoader } from "components/Loader";

/**
 * AiSummaryDisplay - Component for displaying AI summary content
 *
 * @param {String}  summary                 The AI summary text to display
 * @param {Boolean} loading                 Loading state
 * @param {String}  summaryHash             Hash for rating purposes
 * @param {String}  resourceId              Resource ID for ratings and regeneration
 * @param {String}  resourceType            Type of resource (profile, mission, etc.)
 * @param {Boolean} showRegenerateButton    Whether to show regenerate button
 * @param {Boolean} showRatings             Whether to show ratings
 * @param {String}  ratingTextId            Intl message ID for rating text
 */
const AiSummaryDisplay = ({
  summary,
  loading,
  summaryHash,
  resourceId,
  resourceType = RATING_RESOURCE_TYPES.profile,
  showRegenerateButton = false,
  showRatings = true,
  ratingTextId = "AISummary.AIRating.Text",
}) => {
  if (loading) return <Loader />;

  if (!summary) return null;

  return (
    <>
      <div css={styles.summary}>
        <Markdown>{summary}</Markdown>
      </div>
      {(showRegenerateButton || showRatings) && (
        <>
          <hr css={styles.hr} />
          <div css={styles.summary_footer}>
            {showRegenerateButton && <RegenerateAiSummaryButton profileId={resourceId} />}
            {showRatings && (
              <AiRating
                text={<FormattedMessage id={ratingTextId} />}
                resourceType={resourceType}
                resourceId={resourceId}
                contentType={RATING_CONTENT_TYPES.summary}
                contentHash={summaryHash}
                positionRight
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

const Loader = () => (
  <div css={styles.loader}>
    <TextLoader />
    <TextLoader />
  </div>
);

const styles = {
  loader: css`
    display: flex;
    flex-direction: column;
    gap: ${margins.xxsmall};
  `,
  summary: css`
    color: ${colors.purpleRainDark2};
    font-family: "RobotoMono", monospace;
    line-height: 1.5;

    strong {
      font-weight: 700; /* Needed as there is a global style that overrides strong tags */
    }
  `,
  summary_footer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  hr: css`
    margin-left: ${margins.negative};
    margin-right: ${margins.negative};
    width: "calc(100% + 6rem)";
  `,
};

AiSummaryDisplay.propTypes = {
  summary: PropTypes.string,
  loading: PropTypes.bool,
  summaryHash: PropTypes.string,
  resourceId: PropTypes.string,
  resourceType: PropTypes.string,
  showRegenerateButton: PropTypes.bool,
  showRatings: PropTypes.bool,
  ratingTextId: PropTypes.string,
};

export default AiSummaryDisplay;
